import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedSwitch } from './ThemedSwitch';
import { getFirestoreDb } from '../../utils/calendarShareService';
import {
  DAY_SCHEDULE_MODES,
  DEFAULT_RIDER_REMINDER,
  SHARE_SCHEDULE_MODULE_OPTIONS,
  formatGlobalTourScheduleSummary,
  getDayScheduleDisplayStatus,
  getDayScheduleStatusParts,
  resolveDaySchedule,
  saveDayScheduleOverride,
  clearDayScheduleOverride,
} from '../../utils/tourShareScheduleService';

function modeChipLabel(mode) {
  if (mode === DAY_SCHEDULE_MODES.CUSTOM) return 'Custom';
  if (mode === DAY_SCHEDULE_MODES.DISABLED) return 'Disabled';
  return 'Tour default';
}

/** Renders schedule status with send date in bold blue when enabled. */
export function DayScheduleStatusMessage({ settings, dateKey, isShowDay = false, style, theme }) {
  const parts = useMemo(
    () => getDayScheduleStatusParts(settings, dateKey, { isShowDay }),
    [settings, dateKey, isShowDay]
  );

  if (parts.variant === 'enabled' && parts.sendDateLabel) {
    return (
      <Text style={style}>
        {parts.beforeSendDate}
        <Text style={{ color: theme.brand.primary, fontWeight: '800' }}>{parts.sendDateLabel}</Text>
        {parts.afterSendDate}
      </Text>
    );
  }

  return <Text style={style}>{parts.text}</Text>;
}

/** Compact status chip — lives in the day header; tap to expand day schedule settings. */
export function DayScheduleStatusChip({
  dateKey,
  tourShareSettings,
  isShowDay = false,
  expanded = false,
  onPress,
  style,
  inline = false,
}) {
  const { theme, isDarkMode } = useTheme();
  const styles = useMemo(() => createChipStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!dateKey) return null;

  const { statusWord, statusColor } = getDayScheduleDisplayStatus(tourShareSettings, dateKey, { isShowDay });

  return (
    <TouchableOpacity
      style={[styles.chip, inline && styles.chipInline, style]}
      onPress={onPress}
      activeOpacity={0.85}
      accessibilityLabel={`Schedule for this day: ${statusWord}. ${expanded ? 'Collapse' : 'Expand'} settings.`}
      accessibilityRole="button"
    >
      <Ionicons
        name="notifications-outline"
        size={12}
        color={statusColor}
        style={styles.chipIcon}
      />
      <Text style={styles.chipPrefix} numberOfLines={1}>
        Schedule send
      </Text>
      <Text style={[styles.chipStatus, { color: statusColor }]} numberOfLines={1}>
        {statusWord}
      </Text>
      <Ionicons
        name={expanded ? 'chevron-up' : 'chevron-down'}
        size={11}
        color={isDarkMode ? '#a0a0a0' : theme.text.tertiary}
        style={styles.chipChevron}
      />
    </TouchableOpacity>
  );
}

export function DayScheduleSettingsPanel({
  dateKey,
  ownerId,
  tourId,
  tourShareSettings,
  isShowDay = false,
  onToast,
  onOpenTourSettings,
  compact = false,
  expanded = false,
  onExpandedChange,
}) {
  const { theme, isDarkMode } = useTheme();
  const db = getFirestoreDb();
  const override = tourShareSettings?.dayScheduleOverrides?.[dateKey] || null;
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const resolved = useMemo(
    () => resolveDaySchedule(tourShareSettings, dateKey, { isShowDay }),
    [tourShareSettings, dateKey, isShowDay]
  );

  const displayStatus = useMemo(
    () => getDayScheduleDisplayStatus(tourShareSettings, dateKey, { isShowDay }),
    [tourShareSettings, dateKey, isShowDay]
  );

  const globalSummary = useMemo(
    () => formatGlobalTourScheduleSummary(tourShareSettings),
    [tourShareSettings]
  );

  useEffect(() => {
    const mode = override?.mode || DAY_SCHEDULE_MODES.INHERIT;
    setDraft({
      mode,
      daysBefore: String(
        override?.daysBefore != null ? override.daysBefore : tourShareSettings?.riderReminder?.daysBefore ?? 1
      ),
      modules:
        override?.modules?.length
          ? [...override.modules]
          : [...(tourShareSettings?.riderReminder?.modules || DEFAULT_RIDER_REMINDER.modules)],
      email:
        override?.channels?.email != null
          ? override.channels.email
          : tourShareSettings?.riderReminder?.email !== false,
      inApp:
        override?.channels?.inApp != null
          ? override.channels.inApp
          : tourShareSettings?.riderReminder?.inApp !== false,
      recipientEmails: (override?.recipientEmails || []).join(', '),
      label: override?.label || '',
    });
  }, [dateKey, override, tourShareSettings]);

  const collapse = useCallback(() => {
    onExpandedChange?.(false);
  }, [onExpandedChange]);

  const toggleModule = (mod) => {
    setDraft((prev) => {
      if (!prev || prev.mode !== DAY_SCHEDULE_MODES.CUSTOM) return prev;
      const has = prev.modules.includes(mod);
      const next = has ? prev.modules.filter((m) => m !== mod) : [...prev.modules, mod];
      return { ...prev, modules: next.length ? next : [mod] };
    });
  };

  const saveDraft = useCallback(async () => {
    if (!ownerId || !tourId || !dateKey || !draft) return;
    try {
      setSaving(true);
      if (draft.mode === DAY_SCHEDULE_MODES.INHERIT) {
        await clearDayScheduleOverride(db, ownerId, tourId, dateKey);
        onToast?.('Using tour default for this day.', 'success');
      } else {
        await saveDayScheduleOverride(db, ownerId, tourId, dateKey, {
          mode: draft.mode,
          daysBefore:
            draft.mode === DAY_SCHEDULE_MODES.CUSTOM
              ? Math.max(0, Number(draft.daysBefore) || 1)
              : null,
          modules: draft.mode === DAY_SCHEDULE_MODES.CUSTOM ? draft.modules : null,
          channels:
            draft.mode === DAY_SCHEDULE_MODES.CUSTOM
              ? { email: draft.email, inApp: draft.inApp }
              : null,
          recipientEmails: String(draft.recipientEmails || '')
            .split(/[,;\s]+/)
            .map((e) => e.trim())
            .filter(Boolean),
          label: draft.label.trim(),
        });
        onToast?.('Day schedule saved.', 'success');
      }
      collapse();
    } catch (err) {
      onToast?.(err?.message || 'Could not save day schedule.', 'error');
    } finally {
      setSaving(false);
    }
  }, [db, ownerId, tourId, dateKey, draft, onToast, collapse]);

  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!dateKey || !ownerId || !tourId || !draft || !expanded) return null;

  return (
    <View style={[styles.wrap, compact && styles.wrapCompact]}>
      <TouchableOpacity style={styles.headerRow} onPress={collapse} activeOpacity={0.85}>
        <View style={{ flex: 1, gap: 2 }}>
          <View style={styles.headerTitleRow}>
            <Text style={styles.headerTitle}>Schedule for this day</Text>
            <Text style={[styles.headerStatus, { color: displayStatus.statusColor }]}>
              {displayStatus.statusWord}
            </Text>
          </View>
          <DayScheduleStatusMessage
            settings={tourShareSettings}
            dateKey={dateKey}
            isShowDay={isShowDay}
            style={styles.summaryText}
            theme={theme}
          />
        </View>
        <Ionicons name="chevron-up" size={16} color={isDarkMode ? '#c8c8c8' : theme.text.secondary} />
      </TouchableOpacity>

      {resolved.isException ? (
        <View style={styles.exceptionBadge}>
          <Text style={styles.exceptionBadgeText}>
            Exception · {modeChipLabel(resolved.mode)}
          </Text>
        </View>
      ) : null}

      <View style={styles.editor}>
        {!isShowDay ? (
          <Text style={styles.hint}>
            This date is not on the tour show schedule. You can still save a custom send rule if needed.
          </Text>
        ) : (
          <View style={styles.globalHintBox}>
            <Text style={styles.globalHintLabel}>Tour default (global)</Text>
            <Text style={styles.globalHintText}>{globalSummary}</Text>
            <TouchableOpacity
              onPress={() => {
                if (onOpenTourSettings) onOpenTourSettings();
                else onToast?.('Open Tour settings from the sidebar to edit global defaults.', 'success');
              }}
            >
              <Text style={styles.globalHintLink}>Edit in Tour settings</Text>
            </TouchableOpacity>
          </View>
        )}

        <Text style={styles.fieldLabel}>This day</Text>
        <View style={styles.modeRow}>
          {[
            { id: DAY_SCHEDULE_MODES.INHERIT, label: 'Tour default' },
            { id: DAY_SCHEDULE_MODES.CUSTOM, label: 'Custom' },
            { id: DAY_SCHEDULE_MODES.DISABLED, label: 'Disabled', danger: true },
          ].map((opt) => (
            <TouchableOpacity
              key={opt.id}
              style={[styles.modeChip, draft.mode === opt.id && styles.modeChipActive]}
              onPress={() => setDraft((p) => ({ ...p, mode: opt.id }))}
            >
              <Text
                style={[
                  styles.modeChipText,
                  draft.mode === opt.id && styles.modeChipTextActive,
                  opt.danger && draft.mode === opt.id && styles.modeChipTextDanger,
                  opt.danger && draft.mode !== opt.id && styles.modeChipTextDangerMuted,
                ]}
              >
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {draft.mode === DAY_SCHEDULE_MODES.INHERIT && isShowDay ? (
          <Text style={styles.inheritHint}>
            Using the tour default for this show day. Choose Custom to change timing, sections, or channels for this day only, or Disabled to skip this day.
          </Text>
        ) : null}

        {draft.mode === DAY_SCHEDULE_MODES.CUSTOM ? (
          <>
            <Text style={styles.fieldLabel}>Notify how many days before?</Text>
            <TextInput
              style={styles.input}
              value={draft.daysBefore}
              onChangeText={(t) => setDraft((p) => ({ ...p, daysBefore: t.replace(/\D/g, '') || '0' }))}
              keyboardType="number-pad"
              placeholderTextColor={theme.input.placeholder}
            />
            <Text style={styles.fieldLabel}>Sections</Text>
            <View style={styles.moduleGrid}>
              {SHARE_SCHEDULE_MODULE_OPTIONS.map((mod) => {
                const on = draft.modules.includes(mod);
                return (
                  <TouchableOpacity
                    key={mod}
                    style={[styles.moduleChip, on && styles.moduleChipActive]}
                    onPress={() => toggleModule(mod)}
                  >
                    <Text style={[styles.moduleChipText, on && styles.moduleChipTextActive]}>{mod}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>In-app</Text>
              <ThemedSwitch value={draft.inApp} onValueChange={(v) => setDraft((p) => ({ ...p, inApp: v }))} />
            </View>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>Email</Text>
              <ThemedSwitch value={draft.email} onValueChange={(v) => setDraft((p) => ({ ...p, email: v }))} />
            </View>
            <Text style={styles.fieldLabel}>Recipients (optional)</Text>
            <TextInput
              style={styles.input}
              value={draft.recipientEmails}
              onChangeText={(t) => setDraft((p) => ({ ...p, recipientEmails: t }))}
              placeholder="email@example.com, …"
              placeholderTextColor={theme.input.placeholder}
              autoCapitalize="none"
            />
          </>
        ) : null}

        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
            onPress={saveDraft}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.saveBtnText}>Save day schedule</Text>
            )}
          </TouchableOpacity>
          {override ? (
            <TouchableOpacity
              style={styles.resetBtn}
              onPress={() => {
                setDraft((p) => ({ ...p, mode: DAY_SCHEDULE_MODES.INHERIT }));
              }}
              disabled={saving}
            >
              <Text style={styles.resetBtnText}>Reset to tour default</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function createChipStyles(theme, isDarkMode) {
  const panelBg = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  return StyleSheet.create({
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'stretch',
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: panelBg,
    },
    chipInline: {
      alignSelf: 'flex-start',
      paddingHorizontal: 8,
      paddingVertical: 5,
    },
    chipIcon: {
      flexShrink: 0,
    },
    chipPrefix: {
      fontSize: 10,
      fontWeight: '700',
      color: isDarkMode ? '#a0a0a0' : theme.text.tertiary,
      letterSpacing: 0.2,
      flexShrink: 0,
    },
    chipStatus: {
      fontSize: 11,
      fontWeight: '800',
      flex: 1,
      minWidth: 0,
      textAlign: 'right',
    },
    chipChevron: {
      flexShrink: 0,
      marginLeft: 2,
    },
  });
}

function createStyles(theme, isDarkMode) {
  const textPrimary = isDarkMode ? '#f2f2f2' : theme.text.primary;
  const textSecondary = isDarkMode ? '#c8c8c8' : theme.text.secondary;
  const textTertiary = isDarkMode ? '#a0a0a0' : theme.text.tertiary;
  const panelBg = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  return StyleSheet.create({
    wrap: {
      marginHorizontal: 12,
      marginTop: 6,
      marginBottom: 4,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: panelBg,
      overflow: 'hidden',
    },
    wrapCompact: { marginHorizontal: 0 },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
      padding: 10,
    },
    headerTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    headerTitle: {
      fontSize: 11,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    headerStatus: {
      fontSize: 11,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    summaryText: {
      fontSize: 12,
      lineHeight: 17,
      color: textSecondary,
    },
    exceptionBadge: {
      alignSelf: 'flex-start',
      marginLeft: 10,
      marginBottom: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: `${theme.status?.warning || '#f59e0b'}22`,
    },
    exceptionBadgeText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.status?.warning || '#f59e0b',
    },
    editor: {
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      padding: 10,
      gap: 8,
    },
    hint: { fontSize: 11, color: textTertiary, lineHeight: 16 },
    globalHintBox: {
      gap: 4,
      padding: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
    globalHintLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    globalHintText: { fontSize: 11, lineHeight: 16, color: textSecondary },
    globalHintLink: { fontSize: 11, fontWeight: '700', color: theme.brand.primary, marginTop: 2 },
    inheritHint: { fontSize: 11, lineHeight: 16, color: textSecondary },
    fieldLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    modeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    modeChip: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    modeChipActive: {
      borderColor: theme.brand.accent,
      backgroundColor: `${theme.brand.accent}18`,
    },
    modeChipText: { fontSize: 11, fontWeight: '700', color: textSecondary },
    modeChipTextActive: { color: theme.brand.accent },
    modeChipTextDanger: { color: '#ef4444' },
    modeChipTextDangerMuted: { color: '#ef4444', opacity: 0.72 },
    input: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      color: theme.input.text,
      backgroundColor: theme.input.background,
      fontSize: 13,
    },
    moduleGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    moduleChip: {
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    moduleChipActive: { borderColor: theme.brand.accent, backgroundColor: `${theme.brand.accent}18` },
    moduleChipText: { fontSize: 10, fontWeight: '700', color: textSecondary },
    moduleChipTextActive: { color: theme.brand.accent },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 4,
    },
    rowLabel: { fontSize: 13, color: textPrimary },
    actions: { gap: 8, marginTop: 4 },
    saveBtn: {
      backgroundColor: theme.brand.accent,
      borderRadius: 8,
      paddingVertical: 9,
      alignItems: 'center',
    },
    saveBtnDisabled: { opacity: 0.55 },
    saveBtnText: { color: '#fff', fontWeight: '800', fontSize: 12 },
    resetBtn: { alignItems: 'center', paddingVertical: 6 },
    resetBtnText: { fontSize: 11, fontWeight: '700', color: textSecondary },
  });
}
